/**
 * 해시 앵커로 "레이아웃이 정착한 뒤" 1회 스크롤한다.
 *
 * 문제: 다른 페이지에서 `/#price` 로 진입하면 라우터가 전환 직후 좌표를 한 번만 계산하는데,
 * 이때 위쪽 콘텐츠(슬라이더·이미지·비동기 렌더)가 아직 최종 높이를 잡지 못해 대상 요소가
 * 이후 아래로 밀린다 → 사용자는 목표보다 위에 멈춘다(레이아웃 시프트 race).
 *
 * 해법(임의 지연 아님): 대상의 문서상 절대 top 을 rAF 마다 측정해, 값이 연속으로 안정될
 * 때(=레이아웃 정착)만 스크롤한다. 안정되지 않으면 stableFrames 누적이 리셋되고, 전체는
 * timeoutMs 로 상한을 둔다(이미지 미로딩 등으로 영영 안정 안 되는 경우의 안전판).
 *
 * 새 호출은 진행 중인 이전 호출을 대체한다(같은 대상을 여러 곳에서 요청해도 스크롤은 한 번).
 *
 * @param hash       '#price' 형태(또는 'price'). 빈 값/미존재 요소는 무시.
 * @param opts.stableFrames  몇 프레임 연속 동일하면 "정착"으로 볼지(기본 3).
 * @param opts.timeoutMs     요소가 보인 뒤, 정착을 못 기다려도 마지막 좌표로 스크롤하는 상한(기본 1200ms).
 * @param opts.elementWaitMs 요소가 아직 한 번도 안 붙은 동안만 폴링하는 상한(기본 600ms). 영영
 *                           안 나타날 id 에 대한 장시간 rAF 를 막는다. 한 번 보인 뒤엔 timeoutMs 적용.
 * @param opts.behavior      'smooth' | 'auto'(기본 'smooth').
 */
/** 진행 중인 호출 식별자 — 새 호출이 오면 이전 호출의 rAF 루프는 스스로 멈춘다. */
let activeRun = 0;

export function scrollToHash(
  hash: string,
  opts: {
    stableFrames?: number;
    timeoutMs?: number;
    elementWaitMs?: number;
    behavior?: ScrollBehavior;
  } = {},
): void {
  const id = hash.replace(/^#/, '');
  if (!id) return;

  const { stableFrames = 3, timeoutMs = 1200, elementWaitMs = 600, behavior = 'smooth' } = opts;

  const run = ++activeRun;
  const startedAt = performance.now();
  let lastTop = Number.NaN;
  let stableCount = 0;
  /** 요소가 처음 보인 시각 — 정착 타임아웃(timeoutMs)의 기준점. 0 이면 아직 안 보임. */
  let settleStartedAt = 0;

  /** 대상의 문서 기준 절대 top(반올림 — 서브픽셀 떨림이 정착을 방해하지 않게). */
  function measure(el: HTMLElement): number {
    return Math.round(el.getBoundingClientRect().top + window.scrollY);
  }

  /** sticky 헤더 보정은 CSS `scroll-padding-top`(styles/index.css, --spacing-header 기준)이 담당한다. */
  function finalScroll(el: HTMLElement): void {
    el.scrollIntoView({ behavior, block: 'start' });
  }

  function tick(): void {
    if (run !== activeRun) return;
    const now = performance.now();
    const el = document.getElementById(id);
    if (!el) {
      // 요소가 한 번 보였다 사라졌으면(라우트 이탈 등) 더 기다릴 이유가 없다 → 즉시 중단.
      // 아직 한 번도 안 붙었으면(cold-load lazy 마운트 대기) elementWaitMs 까지만 폴링한다 —
      // 정착 대기용 timeoutMs 와 분리해, 영영 안 나타날 id 에 대한 장시간 rAF 를 막는다.
      if (settleStartedAt === 0 && now - startedAt < elementWaitMs) requestAnimationFrame(tick);
      return;
    }
    // 요소가 처음 보인 프레임부터 정착 타임아웃을 잰다(늦게 마운트돼도 정착 대기 시간이 깎이지 않게).
    if (settleStartedAt === 0) settleStartedAt = now;

    const top = measure(el);
    if (top === lastTop) {
      stableCount += 1;
    } else {
      stableCount = 0;
      lastTop = top;
    }

    if (stableCount >= stableFrames || now - settleStartedAt >= timeoutMs) {
      finalScroll(el);
      return;
    }
    requestAnimationFrame(tick);
  }

  requestAnimationFrame(tick);
}
